import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

const tierInfo = {
  founding: { name: 'Founding', price: '$500', color: 'teal' },
  community: { name: 'Community', price: '$1,500', color: 'teal' },
  category: { name: 'Category', price: 'Custom', color: 'gold' },
  presenting: { name: 'Presenting', price: 'Custom', color: 'orange' },
};

export default function SponsorModal({ tier, isOpen, onClose }) {
  const [formData, setFormData] = useState({
    contactName: '',
    phone: '',
    email: '',
    company: '',
    industry: '',
    hasVideoAssets: '',
    hasPhotoAssets: '',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const info = tierInfo[tier] || tierInfo.founding;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      // Save to Firestore
      await addDoc(collection(db, 'sponsor_signups'), {
        ...formData,
        tier,
        tierName: info.name,
        tierPrice: info.price,
        createdAt: serverTimestamp(),
      });

      // Write to mail collection — picked up by Firebase Trigger Email extension
      await addDoc(collection(db, 'mail'), {
        to: 'xavier@watchmindi.tv',
        message: {
          subject: `New ${info.name} Sponsor Signup — ${formData.company} (${info.price})`,
          text: [
            `New ${info.name} Sponsor Signup (${info.price})`,
            '',
            `Contact: ${formData.contactName}`,
            `Email: ${formData.email}`,
            `Phone: ${formData.phone}`,
            `Company: ${formData.company}`,
            `Industry: ${formData.industry}`,
            `Video Assets: ${formData.hasVideoAssets}`,
            `Photo Assets: ${formData.hasPhotoAssets}`,
            `Notes: ${formData.notes || 'None'}`,
            '',
            'This submission has been saved to Firestore.',
          ].join('\n'),
        },
      });

      // For founding/community — redirect to Stripe checkout
      if (tier === 'founding' || tier === 'community') {
        try {
          const res = await fetch('/.netlify/functions/sponsor-checkout', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              tier,
              email: formData.email,
              company: formData.company,
            }),
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data.error);
          window.location.href = data.url;
          return;
        } catch (checkoutErr) {
          setError('Unable to start checkout. Please try again or email synemafest@gmail.com.');
          setSubmitting(false);
          return;
        }
      }

      setSubmitted(true);
    } catch (err) {
      setError('Something went wrong. Please try again or email synemafest@gmail.com.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setFormData({
        contactName: '',
        phone: '',
        email: '',
        company: '',
        industry: '',
        hasVideoAssets: '',
        hasPhotoAssets: '',
        notes: '',
      });
      setSubmitted(false);
      setError(null);
      onClose();
    }
  };

  const accentColor = info.color === 'teal' ? 'text-teal' : info.color === 'gold' ? 'text-gold' : 'text-orange';
  const borderColor = info.color === 'teal' ? 'border-teal/30' : info.color === 'gold' ? 'border-gold/30' : 'border-orange/30';
  const btnClass = info.color === 'teal' ? 'bg-teal' : info.color === 'gold' ? 'bg-gold' : 'btn-gradient';

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 z-50"
            onClick={handleClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.95 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className={`bg-dark2 border ${borderColor} rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto relative`}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button */}
              <button
                onClick={handleClose}
                className="absolute top-4 right-4 text-muted hover:text-white transition-colors text-xl"
                aria-label="Close"
              >
                ×
              </button>

              <div className="p-8">
                {/* Header */}
                <div className={`font-mono text-[0.65rem] tracking-[0.2em] uppercase ${accentColor} mb-2`}>
                  {info.name} Sponsor — {info.price}
                </div>
                <h2 className="font-display text-2xl tracking-[0.04em] text-white mb-6">
                  GET STARTED
                </h2>

                {submitted ? (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center py-8"
                  >
                    <div className={`w-16 h-16 rounded-full bg-teal/20 border border-teal/30 flex items-center justify-center mx-auto mb-6`}>
                      <svg className="w-8 h-8 text-teal" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h3 className="font-display text-xl text-white mb-3">APPLICATION RECEIVED</h3>
                    <p className="text-[0.9rem] font-light text-white/60 leading-relaxed">
                      Thanks for your interest in the {info.name} tier! We'll be in touch within 48 hours at <strong className="text-teal">{formData.email}</strong>.
                    </p>
                    <p className="text-[0.75rem] text-muted mt-3">
                      Don't see our email? Check your spam or promotions folder for a message from synemafest@gmail.com.
                    </p>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {error && (
                      <div className="bg-red-500/10 border border-red-500/20 rounded p-3 text-[0.85rem] text-red-400">
                        {error}
                      </div>
                    )}

                    <div>
                      <label className="block font-mono text-[0.6rem] tracking-[0.2em] uppercase text-muted mb-1.5">
                        Contact Name *
                      </label>
                      <input
                        type="text"
                        name="contactName"
                        required
                        value={formData.contactName}
                        onChange={handleChange}
                        className="w-full bg-dark3 border border-white/[0.06] rounded px-4 py-2.5 text-[0.88rem] text-white/90 font-light focus:border-teal/40 focus:outline-none transition-colors"
                        placeholder="Full name"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block font-mono text-[0.6rem] tracking-[0.2em] uppercase text-muted mb-1.5">
                          Email *
                        </label>
                        <input
                          type="email"
                          name="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          className="w-full bg-dark3 border border-white/[0.06] rounded px-4 py-2.5 text-[0.88rem] text-white/90 font-light focus:border-teal/40 focus:outline-none transition-colors"
                          placeholder="you@company.com"
                        />
                      </div>
                      <div>
                        <label className="block font-mono text-[0.6rem] tracking-[0.2em] uppercase text-muted mb-1.5">
                          Phone *
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          required
                          value={formData.phone}
                          onChange={handleChange}
                          className="w-full bg-dark3 border border-white/[0.06] rounded px-4 py-2.5 text-[0.88rem] text-white/90 font-light focus:border-teal/40 focus:outline-none transition-colors"
                          placeholder="+1 (555) 000-0000"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block font-mono text-[0.6rem] tracking-[0.2em] uppercase text-muted mb-1.5">
                          Company *
                        </label>
                        <input
                          type="text"
                          name="company"
                          required
                          value={formData.company}
                          onChange={handleChange}
                          className="w-full bg-dark3 border border-white/[0.06] rounded px-4 py-2.5 text-[0.88rem] text-white/90 font-light focus:border-teal/40 focus:outline-none transition-colors"
                          placeholder="Company name"
                        />
                      </div>
                      <div>
                        <label className="block font-mono text-[0.6rem] tracking-[0.2em] uppercase text-muted mb-1.5">
                          Industry *
                        </label>
                        <input
                          type="text"
                          name="industry"
                          required
                          value={formData.industry}
                          onChange={handleChange}
                          className="w-full bg-dark3 border border-white/[0.06] rounded px-4 py-2.5 text-[0.88rem] text-white/90 font-light focus:border-teal/40 focus:outline-none transition-colors"
                          placeholder="e.g., Entertainment, Tech"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block font-mono text-[0.6rem] tracking-[0.2em] uppercase text-muted mb-1.5">
                          Have video assets? *
                        </label>
                        <select
                          name="hasVideoAssets"
                          required
                          value={formData.hasVideoAssets}
                          onChange={handleChange}
                          className="w-full bg-dark3 border border-white/[0.06] rounded px-4 py-2.5 text-[0.88rem] text-white/90 font-light focus:border-teal/40 focus:outline-none transition-colors appearance-none"
                        >
                          <option value="">Select</option>
                          <option value="yes">Yes</option>
                          <option value="no">No</option>
                          <option value="in_progress">In progress</option>
                        </select>
                      </div>
                      <div>
                        <label className="block font-mono text-[0.6rem] tracking-[0.2em] uppercase text-muted mb-1.5">
                          Have photo assets? *
                        </label>
                        <select
                          name="hasPhotoAssets"
                          required
                          value={formData.hasPhotoAssets}
                          onChange={handleChange}
                          className="w-full bg-dark3 border border-white/[0.06] rounded px-4 py-2.5 text-[0.88rem] text-white/90 font-light focus:border-teal/40 focus:outline-none transition-colors appearance-none"
                        >
                          <option value="">Select</option>
                          <option value="yes">Yes</option>
                          <option value="no">No</option>
                          <option value="in_progress">In progress</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block font-mono text-[0.6rem] tracking-[0.2em] uppercase text-muted mb-1.5">
                        Anything else we should know?
                      </label>
                      <textarea
                        name="notes"
                        rows={3}
                        value={formData.notes}
                        onChange={handleChange}
                        className="w-full bg-dark3 border border-white/[0.06] rounded px-4 py-2.5 text-[0.88rem] text-white/90 font-light focus:border-teal/40 focus:outline-none transition-colors resize-none"
                        placeholder="Campaign goals, questions, etc."
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={submitting}
                      className={`w-full ${btnClass} text-dark font-body text-[0.78rem] font-semibold tracking-[0.08em] uppercase py-3.5 px-6 rounded-sm transition-all ${
                        submitting ? 'opacity-60 cursor-wait' : 'hover:opacity-90 hover:-translate-y-[1px]'
                      }`}
                    >
                      {submitting
                        ? (tier === 'founding' || tier === 'community') ? 'Redirecting to checkout...' : 'Submitting...'
                        : (tier === 'founding' || tier === 'community') ? `Pay ${info.price} & Get Started` : `Apply for ${info.name} — ${info.price}`}
                    </button>

                    <p className="text-[0.72rem] text-muted text-center mt-2">
                      Or email <a href="mailto:synemafest@gmail.com" className="text-teal">synemafest@gmail.com</a> directly
                    </p>
                  </form>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
